import type { TimeStyle } from 'components/datatable/datatable.types';
import deepFreeze from 'deep-freeze';
import { Duration as LuxonDuration } from 'luxon';
import type { Duration } from 'types/units';
import { timestamp } from 'utils';
import { padNumber } from './numbers';

// Calculate duration object based on base minutes and running timer.
export const ZERO_DURATION: Duration = deepFreeze({
  formatted: '00:00',
  hours: 0,
  minutes: 0,
  seconds: 0,
  total_hours: 0,
  total_minutes: 0,
  total_seconds: 0,
});

export const durationFromHours = (hours: number): Duration => {
  return calculateDuration(LuxonDuration.fromObject({ hours }).as('seconds'));
};

export const durationFromMinutes = (minutes: number): Duration => {
  return calculateDuration(LuxonDuration.fromObject({ minutes }).as('seconds'));
};

export function durationFromHHMMSS(hours: number, minutes: number, seconds: number = 0) {
  if (hours != null && minutes != null && seconds != null) {
    const durationSeconds = hours * 60 * 60 + minutes * 60 + seconds;
    return calculateDuration(durationSeconds);
  }
  return calculateDuration(0);
}

export function calculateDuration(
  baseSeconds: number = 0,
  timerStartedOn: number | null = null,
  timeNow: number | null = null
): Duration {
  if (baseSeconds === 0 && timerStartedOn == null) {
    return ZERO_DURATION;
  }
  const timeNowInUnix = timeNow || timestamp(); // Needed for CALCULATE_TIME_AND_COST test

  // NOTE: Only pass 1 value to duration, OR typeof int
  // FLOATING NUMBERS WILL RESULT IN NaN values being returned!!
  // ref https://app.clubhouse.io/timelyapp/story/5906/timer-timer-started-then-stopped-is-reseting-to-0-00-or-rounding-down
  const timerSeconds = timerStartedOn ? timeNowInUnix - timerStartedOn : 0;
  const totalCalculationSeconds = baseSeconds + timerSeconds;
  const baseDuration = LuxonDuration.fromMillis(totalCalculationSeconds * 1000);
  const converted = baseDuration.shiftTo('hours', 'minutes', 'seconds').toObject();

  return {
    formatted: `${padNumber(converted.hours || 0, 2)}:${padNumber(converted.minutes || 0, 2)}`,
    hours: converted.hours || 0,
    minutes: converted.minutes || 0,
    seconds: converted.seconds || 0,
    total_hours: baseDuration.as('hours'),
    total_minutes: baseDuration.as('minutes'),
    total_seconds: baseDuration.as('seconds'),
  };
}

export function formattedDuration(minutes: number, format: TimeStyle) {
  if (minutes == null || isNaN(minutes)) {
    minutes = 0;
  }

  const duration = durationFromMinutes(minutes);
  const paddedMinutes = duration.minutes.toString().padStart(2, '0');

  switch (format) {
    case 'decimal':
      return (minutes / 60).toFixed(2);
    case 'digital':
      return `${duration.hours}:${paddedMinutes}`;

    case 'traditional':
      return `${duration.hours}h ${paddedMinutes}m`;
  }
}
